import {
  Badge,
  colorsTuple,
  createTheme,
  parseThemeColor,
  ThemeIcon,
  type MantineColorsTuple,
  type MantineTheme,
} from "@mantine/core";

export const BRAND_SHADES = {
  50: "#EDF1F9",
  100: "#D6DFF1",
  200: "#AAB9E0",
  300: "#7A8FCB",
  400: "#4C66B2",
  500: "#2B4296",
  600: "#22357A",
  700: "#1A2960",
  800: "#14204C",
  900: "#0E1738",
};

export const NAVY_SHADES = {
  50: "#EDF1F8",
  100: "#CFD6E8",
  200: "#9DA9C9",
  300: "#6372A1",
  400: "#3A4970",
  500: "#1C2B50",
  600: "#13203D",
  700: "#0C1628",
  800: "#070F1C",
  900: "#030612",
};

export const INK_SHADES = {
  0: "#FFFFFF",
  25: "#FAFBFC",
  50: "#F3F5F8",
  100: "#E3E7ED",
  200: "#CDD4DE",
  300: "#9BA6B6",
  400: "#6B7686",
  500: "#4E5766",
  600: "#39414E",
  700: "#252C36",
  800: "#161B22",
  900: "#090C11",
};

export const EMERALD_SHADES = {
  50: "#ECFDF5",
  100: "#D1FAE5",
  200: "#A7F3D0",
  300: "#6EE7B7",
  400: "#34D399",
  500: "#10B981",
  600: "#059669",
  700: "#047857",
  800: "#065F46",
  900: "#064E3B",
};

export const ROSE_SHADES = {
  50: "#FFF1F2",
  100: "#FFE4E6",
  200: "#FECDD3",
  300: "#FDA4AF",
  400: "#FB7185",
  500: "#F43F5E",
  600: "#E11D48",
  700: "#BE123C",
  800: "#9F1239",
  900: "#881337",
};

export const AMBER_SHADES = {
  50: "#FFFBEB",
  100: "#FEF3C7",
  200: "#FDE68A",
  300: "#FCD34D",
  400: "#FBBF24",
  500: "#F59E0B",
  600: "#D97706",
  700: "#B45309",
  800: "#92400E",
  900: "#78350F",
};

export const VIOLET_SHADES = {
  50: "#F5F3FF",
  100: "#EDE9FE",
  200: "#DDD6FE",
  300: "#C4B5FD",
  400: "#A78BFA",
  500: "#8B5CF6",
  600: "#7C3AED",
  700: "#6D28D9",
  800: "#5B21B6",
  900: "#4C1D95",
};

export const SKY_SHADES = {
  50: "#F0F9FF",
  100: "#E0F2FE",
  200: "#BAE6FD",
  300: "#7DD3FC",
  400: "#38BDF8",
  500: "#0EA5E9",
  600: "#0284C7",
  700: "#0369A1",
  800: "#075985",
  900: "#0C4A6E",
};

const shadesToMantineColorsTuple = (
  shades: Record<
    50 | 100 | 200 | 300 | 400 | 500 | 600 | 700 | 800 | 900,
    string
  >,
  { reverse = false } = {}
): MantineColorsTuple => {
  const sortedShades = Object.entries(shades)
    .sort(([a], [b]) => Number(a) - Number(b))
    .filter(([code]) => Number(code) >= 50 && Number(code) <= 900)
    .map(([, color]) => color);

  if (reverse) {
    sortedShades.reverse();
  }

  return colorsTuple(sortedShades);
};

const SHADE_INDEX = {
  50: 0,
  100: 1,
  200: 2,
  300: 3,
  400: 4,
  500: 5,
  600: 6,
  700: 7,
  800: 8,
  900: 9,
} as const;

export const shade = (
  tuple: MantineColorsTuple,
  value: keyof typeof SHADE_INDEX
) => tuple[SHADE_INDEX[value]];

export const themeColors: Record<string, MantineColorsTuple> = {
  // Main shades
  brand: shadesToMantineColorsTuple(BRAND_SHADES),
  navy: shadesToMantineColorsTuple(NAVY_SHADES),
  ink: shadesToMantineColorsTuple(INK_SHADES),
  emerald: shadesToMantineColorsTuple(EMERALD_SHADES),
  rose: shadesToMantineColorsTuple(ROSE_SHADES),
  amber: shadesToMantineColorsTuple(AMBER_SHADES),
  violet: shadesToMantineColorsTuple(VIOLET_SHADES),
  sky: shadesToMantineColorsTuple(SKY_SHADES),

  // Helpers
  success: shadesToMantineColorsTuple(EMERALD_SHADES),
  danger: shadesToMantineColorsTuple(ROSE_SHADES),
  warning: shadesToMantineColorsTuple(AMBER_SHADES),
  accent: shadesToMantineColorsTuple(VIOLET_SHADES),
  error: shadesToMantineColorsTuple(ROSE_SHADES),

  // Only one shade for these colors are they are only meant to be used as a background color for a component, not as a color for components.
  appBackground: colorsTuple(Array.from({ length: 10 }, () => INK_SHADES[25])),
  cardBackground: colorsTuple(Array.from({ length: 10 }, () => INK_SHADES[0])),

  // Retro-compatibility
  primary: shadesToMantineColorsTuple(BRAND_SHADES, { reverse: true }),
  primaryLight: shadesToMantineColorsTuple(BRAND_SHADES, { reverse: true }),
  primaryDark: shadesToMantineColorsTuple(BRAND_SHADES, { reverse: true }),
  secondary: shadesToMantineColorsTuple(NAVY_SHADES, { reverse: true }),
  greyScale: shadesToMantineColorsTuple(INK_SHADES, { reverse: true }),
};

const getTintedVariantColors = (
  theme: MantineTheme,
  color: string | undefined,
  textShade: 6 | 7 = 7
): { background: string; color: string } | null => {
  const parsed = parseThemeColor({
    color: color || theme.primaryColor,
    theme,
  });

  if (!parsed.isThemeColor) {
    return null;
  }

  return {
    background: `var(--mantine-color-${parsed.color}-0)`,
    color: `var(--mantine-color-${parsed.color}-${textShade})`,
  };
};

export const ColorsTheme = createTheme({
  colors: themeColors,
  primaryColor: "brand",
  primaryShade: 5,
  components: {
    Badge: Badge.extend({
      vars: (theme, props) => {
        const colors =
          props.variant === "light" || props.variant === "tag"
            ? getTintedVariantColors(
                theme,
                props.color,
                props.variant === "tag" ? 6 : 7
              )
            : null;

        if (!colors) {
          return { root: {} };
        }

        return {
          root: {
            "--badge-color": colors.color,
            "--badge-bg": colors.background,
          },
        };
      },
    }),
    ThemeIcon: ThemeIcon.extend({
      vars: (theme, props) => {
        const colors =
          props.variant === "light"
            ? getTintedVariantColors(theme, props.color)
            : null;

        if (!colors) {
          return { root: {} };
        }

        return {
          root: {
            "--ti-color": colors.color,
            "--ti-bg": colors.background,
          },
        };
      },
    }),
  },
});
